import { AxiosInstance, AxiosResponse } from "axios"

/**
 * Admin API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

// Define types for the admin API call
export type RequestType = "get" | "post" | "delete"

// AI Usage Metrics Types
export interface TokenMetrics {
  input: number
  output: number
  cached: number
  total: number
}

export interface CostMetrics {
  input: number
  output: number
  cached: number
  total: number
}

export interface UserMetrics {
  conversionCalls: number
  geminiCalls: number
  validationCalls: number
  tokens: TokenMetrics
  costs: CostMetrics
}

export interface UnitMetrics {
  conversionCalls: number
  geminiCalls: number
  validationCalls: number
  tokens: TokenMetrics
  costs: CostMetrics
}

export interface TotalMetrics {
  conversionCalls: number
  geminiCalls: number
  validationCalls: number
  tokens: TokenMetrics
  costs: CostMetrics
}

export interface UsageStats {
  uniqueUsers: number
  uniqueUnits: number
  averageGeminiCallsPerConversion: string
}

export interface SystemMetrics {
  requestCount: number
  errorCount: number
  responseTimeSum: number
  averageResponseTime: number
  errorRate: string
}

export interface GeneralUsageMetrics {
  timestamp: string
  date: string
  period: string
  filters: Record<string, unknown>
  byUser: Record<string, UserMetrics>
  byUnit: Record<string, UnitMetrics>
  total: TotalMetrics
  stats: UsageStats
}

export interface GeneralMetricsResponse {
  timestamp: string
  usage: GeneralUsageMetrics
  system: SystemMetrics
}

export interface TopUserData {
  user: string
  conversionCalls: number
  geminiCalls: number
  totalCalls: number
}

export interface TopUnitData {
  unit: string
  conversionCalls: number
  geminiCalls: number
  totalCalls: number
}

export interface TopUsersResponse {
  timestamp: string
  type: "top-users"
  period: string
  limit: number
  data: TopUserData[]
}

export interface TopUnitsResponse {
  timestamp: string
  type: "top-units"
  period: string
  limit: number
  data: TopUnitData[]
}

export type AiUsageMetricsResponse =
  | GeneralMetricsResponse
  | TopUsersResponse
  | TopUnitsResponse

/**
 * Superadmin only route.
 * call any api.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param route - The route to call.
 * @param requestType - The type of request to make (get, post, delete).
 * @param body - The body of the request, used for POST requests. Should be a JSON string.
 * @returns The response from the API.
 * @throws Will throw an error if the request fails.
 */
export async function callAdminApi(
  axiosInstance: AxiosInstance,
  route: string,
  requestType: RequestType,
  body: string = ""
): Promise<AxiosResponse<unknown>> {
  const config = {
    headers: {
      "Content-Type": "application/json", // Ensure JSON content type
    },
  }

  let response: AxiosResponse<unknown>
  let parsedBody: unknown

  if (body) {
    parsedBody = JSON.parse(body)
  }

  if (requestType === "get") {
    response = await axiosInstance.get(route, config)
  } else if (requestType === "post") {
    response = await axiosInstance.post(route, parsedBody, config)
  } else if (requestType === "delete") {
    response = await axiosInstance.delete(route, config)
  } else {
    throw new Error(`Unsupported request type: ${requestType}`)
  }

  return response
}

/**
 * Superadmin only route.
 * Fetches the list of all units.
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns The response from the API containing the list of available units as a string.
 * @throws Will throw an error if the request fails.
 */
export async function fetchUnits(
  axiosInstance: AxiosInstance
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>("/available-units")
  return response
}

/**
 * Superadmin only route.
 * Fetches AI usage metrics with optional filters.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param queryParams - Optional query parameters for filtering metrics
 * @returns The response from the API containing AI usage metrics
 * @throws Will throw an error if the request fails.
 *
 * @example
 * // Get general metrics for today
 * fetchAiUsageMetrics(axios, { period: 'today' })
 *
 * @example
 * // Get metrics filtered by user
 * fetchAiUsageMetrics(axios, { period: 'today', user: 'richard_yang' })
 *
 * @example
 * // Get top users
 * fetchAiUsageMetrics(axios, { top: 'users', period: 'all-time', limit: 10 })
 */
export async function fetchAiUsageMetrics(
  axiosInstance: AxiosInstance,
  queryParams?: {
    period?: "today" | "all-time" | string // Can be 'today', 'all-time', or YYYY-MM-DD
    user?: string
    unit?: string
    top?: "users" | "units"
    limit?: number
  }
): Promise<AxiosResponse<AiUsageMetricsResponse>> {
  const params = new URLSearchParams()

  if (queryParams) {
    if (queryParams.period) params.append("period", queryParams.period)
    if (queryParams.user) params.append("user", queryParams.user)
    if (queryParams.unit) params.append("unit", queryParams.unit)
    if (queryParams.top) params.append("top", queryParams.top)
    if (queryParams.limit) params.append("limit", queryParams.limit.toString())
  }

  const queryString = params.toString()
  const url = queryString
    ? `/ai-usage-metrics?${queryString}`
    : "/ai-usage-metrics"

  const response = await axiosInstance.get<AiUsageMetricsResponse>(url)
  return response
}
